<template>
	<VerticalLayout>
		<TypographyTitle v-if="!roversHistoryStore.showOnMap" :level="3" class="pd-vertical">{{ $t('rovers.activeSessions.header') }}</TypographyTitle>
		<Row>
			<Col span="24" v-if="!roversHistoryStore.showOnMap">
				<TypographyParagraph type="secondary">{{ $t('rovers.activeSessions.description') }}</TypographyParagraph>
			</Col>
			<VerticalLayout>
				<LabeledSwitch
					v-if="showOnMapAllowed"
					v-model="roversHistoryStore.showOnMap"
					:rightLabel="$t('rovers.activeSessions.showOnMap')"
				/>
				<LabeledSwitch
					v-if="showOnMapAllowed && roversHistoryStore.showOnMap"
					v-model="roversHistoryStore.autoUpdate"
					:rightLabel="$t('rovers.activeSessions.autoUpdate')"
				/>
			</VerticalLayout>
		</Row>
		<Row :gutter="[16, 8]">
			<Col v-bind="tableFilterSizing">
				<Card :bordered="false">
					<VerticalLayout>
						<TypographyTitle v-if="!roversHistoryStore.showOnMap" :level="5">{{ $t('rovers.activeSessions.filtersHeader') }}</TypographyTitle>
						<UserAddrSessFilterStoreSynced
							addressField="ip_address"
							usernameField="username"
							sessionIdField="session_id"
						/>
					</VerticalLayout>
				</Card>
			</Col>
			<Col v-bind="tableSizing">
				<VerticalLayout>
					<div class="scrollable-x">
						<Table
							:columns="columns"
							:data-source="roversHistoryStore.filteredSessions"
							:loading="roversHistoryStore.isLoadingSessions"
							:row-selection="rowSelection"
							size="small"
							row-key="id"
						>
							<template #bodyCell="{ column, record }">
								<template v-if="column.dataIndex === 'quality'">
									<GpsFixQualityDisplay :quality="record.quality" />
								</template>
								<template v-else-if="column.dataIndex === 'updated'">
									<DateTimeDisplay :date="record.updated" />
								</template>
								<template v-else-if="column.dataIndex === 'service'">
									{{ record.service === 1 ? $t('rovers.service.near') : $t('rovers.service.vrs') }}
								</template>
								<template v-else-if="column.dataIndex === 'session_id'">
									<span v-if="!roversHistoryStore.showOnMap" class="dotted" @click="handleSessionIdClick(record.session_id)">#{{ record.session_id }}</span>
									<template v-else>#{{ record.session_id }}</template>
								</template>
							</template>
						</Table>
					</div>
				</VerticalLayout>
			</Col>
		</Row>
		<Row :gutter="[16, 8]">
			<Col v-bind="heatmapFilterSizing">
				<VerticalLayout>
					<Card :bordered="false">
						<VerticalLayout>
							<TypographyParagraph v-if="!roversHistoryStore.showOnMap" type="secondary">
								{{ $t('rovers.activeSessions.stateHistoryChart.hint') }}
							</TypographyParagraph>
							<DateRangeChooser
								ref="dateRangeChooserRef"
								v-model="datesRange"
								showTime
								quickPeriod="minute30"
								:quickPeriods="['minute30', 'hour1', 'hour2', 'hour6', 'hour12', 'hour24']"
								@change="stateHistoryGet"
							/>
						</VerticalLayout>
					</Card>
				</VerticalLayout>
			</Col>
			<Col v-bind="heatmapSizing">
				<HeatMapChart
					v-if="roversHistoryStore.selectedRowKeys.length > 0 && datesRangeStr != null"
					:isLoading="roversHistoryStore.isLoadingStates"
					:series="heatmapSeries"
					:categories="heatmapCategories"
					colorSettingsMode="exact"
					:colorSettings="colorSettings"
					:xAxisLabel="$t('rovers.activeSessions.stateHistoryChart.xAxisLabel')"
					showLabelNamesInTooltip
					:yAxisLabelFormatterFunction="getSessionLabel"
					:yAxisTooltipFormatterFunction="getSessionTooltip"
				/>
				<EmptyChartFiltersMessage v-else :messageKey="'rovers.activeSessions.stateHistoryChart.placeholder'" />
			</Col>
		</Row>
	</VerticalLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { Table, TypographyTitle, TypographyParagraph, Row, Col, Card } from 'ant-design-vue';

import Consts from '@/api/Consts';

import { useI18n } from 'vue-i18n';
import { useRoversHistoryStore } from '@/stores/roversHistoryStore';
import { useInterval } from '@/utils/useInterval';

import { generateSeries, pointEntryFactoryXYDay } from '@/utils/chartsUtils';
import { datetimeToString, dayJsDatetimeToString, getDayJsDiffMinutes } from '@/components/common/dateUtils';
import { addQualityStatsToSeries, aggregateQualitySeriesCategories } from '@/components/roversHistory/heatmapUtils';

import MapHelpers from '@/components/map/utils/MapHelpers';

import VerticalLayout from '@/components/common/VerticalLayout';
import LabeledSwitch from '@/components/common/LabeledSwitch';
import GpsFixQualityDisplay from '@/components/common/GpsFixQualityDisplay';
import DateTimeDisplay from '@/components/common/DateTimeDisplay';
import DateRangeChooser from '@/components/common/DateRangeChooser';
import UserAddrSessFilterStoreSynced from '@/components/common/UserAddrSessFilterStoreSynced.vue';
import EmptyChartFiltersMessage from '@/components/charts/EmptyChartFiltersMessage';
import HeatMapChart from '@/components/charts/HeatMapChart';

export default {
	components: {
		Table, TypographyTitle, TypographyParagraph, Row, Col, Card,
		VerticalLayout, LabeledSwitch, GpsFixQualityDisplay, DateTimeDisplay, DateRangeChooser,
		UserAddrSessFilterStoreSynced, EmptyChartFiltersMessage, HeatMapChart,
	},
	props: {
		onSessionIdClick: {
			type: Function,
			required: false,
		},
	},
	setup(props) {
		const { t } = useI18n();
		const handleSessionIdClick = id => {
			props.onSessionIdClick(id);
		};
		const datesRange = ref(null);
		const dateRangeChooserRef = ref(null);

		const roversHistoryStore = useRoversHistoryStore();

		const getSessionLabels = id => {
			const session = roversHistoryStore.sessions.find(each => each.id == id);
			if (session == null) {
				return {
					label: String(id),
					tooltip: String(id),
				}
			}
			return {
				label: `#${session.session_id} ${session.username}`,
				tooltip: `#${session.session_id} ${session.username}@${session.ip_address}`,
			}
		};
		const getSessionLabel = id => getSessionLabels(id).label;
		const getSessionTooltip = id => getSessionLabels(id).tooltip;
		
		const datesRangeStr = computed(() => {
			if (datesRange.value == null || datesRange.value.length < 2 || datesRange.value[0] == null || datesRange.value[1] == null) {
				return null;
			}
			return datesRange.value.map(dayJsDatetimeToString);
		});

		const stateHistoryGet = () => {
			if (datesRangeStr.value == null) {
				return;
			}
			if (roversHistoryStore.selectedRowKeys.length === 0) {
				roversHistoryStore.resetStateHistory();
				return;
			}
			roversHistoryStore.doRequestStateHistory({
				start_datetime: datesRangeStr.value[0],
				end_datetime: datesRangeStr.value[1],
				rover_session_ids: roversHistoryStore.selectedRowKeys,
			});
		};
		
		const onSelect = keys => {
			roversHistoryStore.setSelectedRowKeys(keys);
			if (datesRangeStr.value != null) {
				stateHistoryGet();
			}
		};

		const rowSelection = computed(() => ({
			selectedRowKeys: roversHistoryStore.selectedRowKeys,
			onChange: onSelect,
			preserveSelectedRowKeys: true,
			selections: [{
				key: 'none',
				text: t('controls.table.deselectAll'),
				onSelect: () => {
					roversHistoryStore.setSelectedRowKeys([]);
				},
			}],
		}));

		const colorSettings = computed(() =>
			Array.from({ length: 10 }, (_, code) => ({
				exactValue: code,
				color: MapHelpers.getGnssFixQualityDisplay(code).color,
				label: t('gpsFixQuality.' + code),
			}))
		);

		const seriesStep = computed(() => {
			if (datesRangeStr.value == null) return 1;
			const diffMinutes = getDayJsDiffMinutes(datesRange.value[0], datesRange.value[1]);
			if (diffMinutes > 24 * 60) return 15;
			if (diffMinutes > 12 * 60) return 10;
			if (diffMinutes > 3 * 60) return 5;
			return 1;
		});

		const heatmapSeriesCategories = computed(() => {
			if (datesRangeStr.value == null) {
				return { series: [], categories: [] };
			}
			const result = generateSeries({
				items: roversHistoryStore.historyStates,
				datetimeField: 'current_datetime',
				getSeriesKeyFn: item => item.rover_session_id,
				getValueFn: item => item.quality,
				seriesStep: 1,
				seriesStepUnit: 'minute',
				categoryFormatter: datetimeToString,
				pointEntryFactory: pointEntryFactoryXYDay,
				showNullAsZero: false,
				minDate: datesRange.value[0].toDate(),
				maxDate: datesRange.value[1].toDate(),
				forceSeriesKeys: roversHistoryStore.selectedRowKeys,
				roundTimeValues: true,
				pushAdditionalFields: ['quality_stats'],
			});
			const aggregatedSeriesCategories = aggregateQualitySeriesCategories(
				result.series,
				seriesStep.value,
				'minute',
				datesRange.value[0].toDate(),
				datesRange.value[1].toDate(),
			);
			return aggregatedSeriesCategories;
		});
		const heatmapSeries = computed(() => addQualityStatsToSeries(heatmapSeriesCategories.value.series, t));
		const heatmapCategories = computed(() => heatmapSeriesCategories.value.categories);

		onMounted(() => {
			roversHistoryStore.doRequestSessions();
		});
		const tableFilterSizing = computed(() => roversHistoryStore.showOnMap ? { span: 24, push: 0 } : { span: 6, push: 18 });
		const tableSizing = computed(() => roversHistoryStore.showOnMap ? { span: 24, pull: 0 } : { span: 18, pull: 6 });
		const heatmapFilterSizing = computed(() => roversHistoryStore.showOnMap ? { span: 24, push: 0 } : { span: 6, push: 18 });
		const heatmapSizing = computed(() => roversHistoryStore.showOnMap ? { span: 24, pull: 0 } : { span: 18, pull: 6 });

		const showOnMapAllowed = ref(true);

		useInterval(() => {
			if (roversHistoryStore.showOnMap && roversHistoryStore.autoUpdate) {
				roversHistoryStore.doRequestSessions();
				dateRangeChooserRef.value.setEndDateNow();
			}
		}, 15000)

		return {
			columns: Consts.activeRoverSessionsTableColumnsComp,
			rowSelection,
			datesRange,
			dateRangeChooserRef,
			datesRangeStr,
			heatmapSeries,
			heatmapCategories,
			getSessionLabel,
			getSessionTooltip,
			colorSettings,
			stateHistoryGet,
			handleSessionIdClick,
			roversHistoryStore,
			tableFilterSizing,
			tableSizing,
			heatmapFilterSizing,
			heatmapSizing,
			showOnMapAllowed,
		};
	}
};
</script>
